/* eslint-disable react/prop-types */

import React, { useState, useEffect, useCallback } from 'react';
import { Button, Col, Row, Table } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import DynamicNormalForm from '../dynamicNormalForm';
import { generateValidationSchema } from '../validationSchema';
import DynamicDropdown from './ModalDropdown';
import { MdDelete, MdEdit } from 'react-icons/md';
import { globalValidations } from 'helpers/utils';

export const ModalForm = ({
  modalConfig,
  show,
  onClose,
  onSubmit,
  initialData = []
}) => {
  const [tableData, setTableData] = useState(initialData);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showModal, setShowModal] = useState(show);
  const [dropdownOptions, setDropdownOptions] = useState({});

  // Create a separate form context for the modal
  const modalMethods = useForm({
    resolver: yupResolver(
      generateValidationSchema({
        fields: modalConfig.fields
      })
    )
  });

  const {
    handleSubmit,
    control,
    register,
    reset,
    formState: { errors },
    setValue
  } = modalMethods;

  // Set initial table data if provided
  useEffect(() => {
    if (initialData?.length > 0) {
      setTableData(initialData);
    }
  }, [initialData]);

  // Handle the show state
  useEffect(() => {
    setShowModal(prev => (prev !== show ? show : prev));
  }, [show]);

  // Reset form when modal closes or submits
  useEffect(() => {
    if (!showModal && editingIndex === null) {
      reset();
    }
  }, [showModal, reset, editingIndex]);

  const handleDropdownOptions = useCallback((fieldId, options) => {
    setDropdownOptions(prev => {
      if (JSON.stringify(prev[fieldId]) !== JSON.stringify(options)) {
        return { ...prev, [fieldId]: options };
      }
      return prev; // Prevents unnecessary re-render
    });
  }, []);

  // function transformData(data) {
  //   let result = {};

  //   for (let key in data) {
  //     if (typeof data[key] === 'object' && data[key] !== null) {
  //       let min = data[key].min;
  //       let max = data[key].max;

  //       if (min !== null && max !== null) {
  //         result[key] = `${min}-${max}`;
  //       } else {
  //         result[key] = '-';
  //       }
  //     } else {
  //       result[key] = data[key]; // Keep other values as is
  //     }
  //   }

  //   return result;
  // }

  const handleFormSubmit = async (data, e, keepModalOpen = false) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await modalMethods.trigger();

      if (modalConfig.validation) {
        // if there is global validations if will check for those validations
        const response = globalValidations(data, modalConfig); // function to check the global validations and returns true if all validation passed or return false if validations fails along with a toast message
        if (!response) return;
      }

      if (Object.keys(modalMethods.formState.errors).length === 0) {
        // Process data to include dropdown labels
        // const labelModifiedData = transformData(data);
        const processedData = processFormData(data);
        let updatedTableData;
        if (editingIndex !== null) {
          updatedTableData = [...tableData];
          updatedTableData[editingIndex] = processedData;
          setTableData(updatedTableData);
          setEditingIndex(null);
        } else {
          updatedTableData = [...tableData, processedData];
          setTableData(updatedTableData);
        }
        if (onSubmit) {
          onSubmit({
            data: updatedTableData,
            modalId: modalConfig.id
          });
        }

        reset(); // Reset form fields after submission

        if (!keepModalOpen) {
          setShowModal(false);
          onClose(false); // Close modal only if keepModalOpen is false
        }
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // Process form data to include dropdown labels
  const processFormData = data => {
    const processedData = { ...data };

    // Find dropdown fields and include their labels
    modalConfig.fields.forEach(field => {
      if (data[field.id] == null || data[field.id] === '') {
        processedData[field.id] = null;
        processedData[`${field.id}_label`] = null;
      }
      if (field.type === 'dropdown' && data[field.id]) {
        const fieldValue = data[field.id];

        // Find the selected option to get its label
        const options = dropdownOptions[field.id] || [];
        const option = options?.find(opt => opt.value === fieldValue);

        if (option) {
          // Add the label for display purposes
          processedData[`${field.id}_label`] = option.label;
        }
      }
    });

    return processedData;
  };

  const handleEdit = index => {
    setEditingIndex(index);
    const rowData = tableData[index];

    // Reset the form first
    reset();

    // Set values for all fields
    Object.keys(rowData).forEach(key => {
      if (!key.endsWith('_label')) {
        const field = modalConfig.fields.find(f => f.id === key);

        if (field?.dependsOn) {
          setTimeout(() => {
            setValue(key, rowData[key]);
          }, 1000);
        } else {
          setValue(key, rowData[key]);
        }
      }
    });

    // Open the modal for editing
    setShowModal(true);
    onClose(true);
  };

  const handleDelete = index => {
    const updatedData = tableData.filter((_, i) => i !== index);
    setTableData(updatedData);
    onSubmit({ data: updatedData, modalId: modalConfig.id });
  };

  const renderField = useCallback(
    field => {
      if (field.type === 'dropdown') {
        return (
          <DynamicDropdown
            key={field.id}
            metadata={field}
            setDropdownOptions={handleDropdownOptions}
          />
        );
      }
      return (
        <DynamicNormalForm
          key={field.id}
          fields={modalConfig.fields}
          metadata={field}
          control={control}
          mode="create"
          register={register}
          errors={errors}
        />
      );
    },
    [modalConfig.fields, control, register, errors, handleDropdownOptions]
  );

  const renderFields = () => {
    return modalConfig.fields.map(field => (
      <Col
        lg={field.type === 'textarea' ? 12 : field.width ? field.width : 6}
        key={field.id}
        className="g-lg-4 g-2"
      >
        {renderField(field)}
      </Col>
    ));
  };

  const renderTable = () => {
    const displayFields = modalConfig.fields.filter(field => !field.hidden);

    return (
      <div className="mt-4 overflow-auto">
        <Table striped bordered hover>
          <thead>
            <tr>
              {displayFields.map(field => (
                <th key={field.id} style={{ fontSize: '0.75rem' }}>
                  {field.label}
                </th>
              ))}
              <th style={{ fontSize: '0.75rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableData.length === 0 ? (
              <tr>
                {displayFields.map(field => (
                  <td key={field.id}>-</td>
                ))}
                <td>-</td>
              </tr>
            ) : (
              tableData.map((row, index) => (
                <tr key={index}>
                  {displayFields.map(field => (
                    <td key={field.id}>
                      {field.type === 'dropdown' ? (
                        row[`${field.id}_label`] || '-'
                      ) : field.type === 'minmax' &&
                        row[field.id] &&
                        typeof row[field.id] === 'object' ? (
                        typeof row[field.id].min === 'number' &&
                        typeof row[field.id].max === 'number' ? (
                          `${row[field.id].min}-${row[field.id].max}`
                        ) : (
                          '-'
                        )
                      ) : field.type === 'file' && row[field.id] ? (
                        <strong>Image Captured ✅</strong>
                      ) : (
                        row[field.id] || '-'
                      )}
                    </td>
                  ))}
                  <td>
                    <span
                      onClick={() => handleEdit(index)}
                      style={{
                        cursor: 'pointer',
                        marginRight: '8px',
                        color: 'green'
                      }}
                    >
                      <MdEdit size={20} />
                    </span>
                    <span
                      onClick={() => handleDelete(index)}
                      style={{ cursor: 'pointer', color: '#C00000' }}
                    >
                      <MdDelete size={20} />
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  const handleModalClose = e => {
    // Prevent event from bubbling up to parent form
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    reset();
    setEditingIndex(null);
    setShowModal(false);
    onClose(false);
  };

  return (
    <>
      {/* Modal */}
      <div
        className={`modal ${showModal ? 'show d-block' : ''}`}
        tabIndex="-1"
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {editingIndex !== null ? 'Edit Entry' : modalConfig.form.title}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleModalClose}
              ></button>
            </div>
            <div className="modal-body">
              {/* Create a new FormProvider context for the modal */}
              <FormProvider {...modalMethods}>
                <form
                  onSubmit={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit(handleFormSubmit)(e);
                  }}
                  noValidate
                  onClick={e => e.stopPropagation()}
                >
                  <Row className="gx-2 gy-3">{renderFields()}</Row>
                  <div className="mt-3">
                    {/* On larger screens: text-end with inline buttons */}
                    <div className="d-none d-md-block text-end">
                      <Button
                        variant="secondary"
                        onClick={handleModalClose}
                        className="me-2"
                      >
                        Cancel
                      </Button>
                      {/* Submit & Close Button */}
                      <Button
                        type="submit"
                        variant="primary"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSubmit(data =>
                            handleFormSubmit(data, e, false)
                          )(e);
                        }}
                      >
                        {editingIndex !== null ? 'Update' : 'Submit'}
                      </Button>

                      {/* Submit & Add Another Button */}
                      <Button
                        type="button"
                        variant="success"
                        className="ms-2"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSubmit(data => handleFormSubmit(data, e, true))(
                            e
                          );
                        }}
                      >
                        Submit & Add Another
                      </Button>
                    </div>

                    {/* On smaller screens: full width buttons with different order */}
                    <div className="d-block d-md-none">
                      {/* Submit & Close Button */}
                      <Button
                        type="submit"
                        variant="primary"
                        className="w-100 mb-2"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSubmit(data =>
                            handleFormSubmit(data, e, false)
                          )(e);
                        }}
                      >
                        {editingIndex !== null ? 'Update' : 'Submit'}
                      </Button>

                      {/* Submit & Add Another Button */}
                      <Button
                        type="button"
                        variant="success"
                        className="w-100 mb-2"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSubmit(data => handleFormSubmit(data, e, true))(
                            e
                          );
                        }}
                      >
                        Submit & Add Another
                      </Button>

                      {/* Cancel Button - Last on mobile */}
                      <Button
                        variant="secondary"
                        onClick={handleModalClose}
                        className="w-100"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
      {showModal && <div className="modal-backdrop show"></div>}

      {/* Table */}
      {renderTable()}
    </>
  );
};
